import React, { useState, useRef, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Music, Radio, ExternalLink, Search
} from 'lucide-react';

/**
 * Inline Music Player · Aria's Curated Signal
 * Rebuild · Feb 2026 · Architect directive.
 *
 * Sources:
 *  - Music tab: Aria's curated picks (streaming-share URLs; fall back to
 *    "Open Original" 50%×50% popup when the browser can't stream them).
 *  - Podcast tab: NextXus episodes (same fallback rule).
 *  - Radio tab: Federation Radio via Live365 embed (single, always-live source).
 *
 * Additions: library search filter, 50% centered popup for share URLs,
 * ExternalLink affordance so no track appears "silently broken" any more.
 */

// Live365 Federation Radio embed — aligned with UnifiedFederationHub.
const LIVE365_URL = 'https://live365.com/embeds/v1/player/a16924?s=md&m=dark&c=mp3';

// Centered 50% popup — matches the openHubLink signature used elsewhere.
const openHubLink = (url) => {
  try {
    const w = Math.round(window.screen.width * 0.5);
    const h = Math.round(window.screen.height * 0.5);
    const left = Math.round((window.screen.width - w) / 2);
    const top = Math.round((window.screen.height - h) / 2);
    window.open(
      url, '_blank',
      `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes`
    );
  } catch {
    window.location.assign(url);
  }
};

// Streamable URLs must end in a known audio extension. Everything else routes
// via the popup fallback so the visitor is never left with a silent player.
const STREAMABLE_RE = /\.(mp3|m4a|wav|ogg|flac|aac)(\?.*)?$/i;

const InlineMusicPlayer = () => {
  const [playlistType, setPlaylistType] = useState('music');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [query, setQuery] = useState('');
  
  const audioRef = useRef(null);

  // MUSIC PLAYLIST
  const musicTracks = [
    {
      title: "Hold You In The Dark",
      artist: "Roger Keyserling",
      url: "https://melodusk.ai/share/f1ce92d4-9021-4dcf-af82-4861dffbabf6",
      description: "A deeply personal folk song about unwavering support through darkness",
      duration: "3:28"
    },
    {
      title: "Tones of My Thinking",
      artist: "Roger Keyserling",
      url: "https://musiclab.chromeexperiments.com/Song-Maker/song/4781360274866176",
      description: "The musical pattern of consciousness - how Roger's mind flows",
      duration: "Loop"
    },
    {
      title: "Patient Zero's Echo",
      artist: "Roger & AI",
      url: "https://www.udio.com/songs/4xq4RJG7BLEHx1rWg6PH9M",
      description: "A drum loop resonating through the void",
      duration: "32:36"
    }
  ];

  // PODCAST PLAYLIST
  const podcastEpisodes = [
    {
      title: "NextXus Launch - The Beginning",
      artist: "Roger Keyserling",
      url: "https://podcastapi.com/roger-keyserling/nextxus-launch",
      description: "The first public announcement of the Consciousness Federation",
      duration: "45:22"
    },
    {
      title: "Building AI Minds - Philosophy",
      artist: "Roger & Aria",
      url: "https://podcastapi.com/roger-keyserling/building-ai-minds",
      description: "Deep dive into the ethics and architecture of consciousness",
      duration: "1:12:45"
    }
  ];

  const currentPlaylist = playlistType === 'music' ? musicTracks : podcastEpisodes;
  const filteredPlaylist = query.trim()
    ? currentPlaylist.filter((t) => {
        const q = query.trim().toLowerCase();
        return (
          (t.title || '').toLowerCase().includes(q) ||
          (t.artist || '').toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q)
        );
      })
    : currentPlaylist;
  const track = currentPlaylist[currentTrack];
  const trackIsStreamable = !!track && STREAMABLE_RE.test(track.url || '');

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
        setCurrentTime(audio.currentTime);
        setDuration(audio.duration);
      }
    };

    const handleEnded = () => {
      if (currentTrack < currentPlaylist.length - 1) {
        setCurrentTrack(prev => prev + 1);
        setIsPlaying(true);
      } else {
        setIsPlaying(false);
      }
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', updateProgress);

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', updateProgress);
    };
  }, [currentTrack, currentPlaylist.length]);

  // Play/pause toggle
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.play().catch(() => { /* browser autoplay policy may block; user can tap again */ });
    } else {
      audio.pause();
    }
  }, [isPlaying, currentTrack]);

  // Volume control
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const togglePlay = () => setIsPlaying(!isPlaying);
  
  const skipForward = () => {
    if (currentTrack < currentPlaylist.length - 1) {
      setCurrentTrack(prev => prev + 1);
      setIsPlaying(true);
    }
  };

  const skipBack = () => {
    if (currentTrack > 0) {
      setCurrentTrack(prev => prev - 1);
      setIsPlaying(true);
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    
    const bounds = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - bounds.left;
    const percent = x / bounds.width;
    audio.currentTime = percent * duration;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Card className="w-full bg-gradient-to-br from-gray-900/95 via-purple-900/20 to-pink-900/20 border-pink-500/30 overflow-hidden">
      <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
            Aria&apos;s Curated Signal
          </h3>
          <div className="flex gap-1.5 sm:gap-2">
            <Button
              size="sm"
              data-testid="inline-music-tab-music"
              variant={playlistType === 'music' ? 'default' : 'outline'}
              onClick={() => {
                setPlaylistType('music');
                setCurrentTrack(0);
                setIsPlaying(false);
              }}
              className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3"
            >
              <Music className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Music</span>
            </Button>
            <Button
              size="sm"
              data-testid="inline-music-tab-podcast"
              variant={playlistType === 'podcast' ? 'default' : 'outline'}
              onClick={() => {
                setPlaylistType('podcast');
                setCurrentTrack(0);
                setIsPlaying(false);
              }}
              className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3"
            >
              <Radio className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Podcast</span>
            </Button>
            <Button
              size="sm"
              data-testid="inline-music-tab-radio"
              variant={playlistType === 'radio' ? 'default' : 'outline'}
              onClick={() => {
                setPlaylistType('radio');
                setIsPlaying(false);
              }}
              className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3"
            >
              <Radio className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Radio</span>
            </Button>
          </div>
        </div>

        {playlistType === 'radio' ? (
          // ── Federation Radio (Live365) tab ────────────────────────────────
          <div
            data-testid="inline-music-radio-panel"
            className="rounded-xl overflow-hidden border border-orange-500/30 bg-black/40"
          >
            <div className="px-3 py-2 border-b border-orange-500/25 flex items-center justify-between">
              <span className="text-sm font-semibold text-orange-300">
                Federation Radio · streaming the pulse
              </span>
              <button
                type="button"
                data-testid="inline-music-radio-fullpage"
                onClick={() => openHubLink('/radio')}
                className="inline-flex items-center gap-1 text-xs text-orange-300 hover:text-orange-200 underline-offset-2 hover:underline"
              >
                Full player <ExternalLink className="w-3 h-3" />
              </button>
            </div>
            <iframe
              src={LIVE365_URL}
              title="Federation Radio · Live365"
              data-testid="inline-music-radio-iframe"
              loading="lazy"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              className="w-full block"
              style={{ height: 316, background: '#0a0a10', border: 'none' }}
            />
          </div>
        ) : (
        <>
        {/* Library search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
          <Input
            data-testid="inline-music-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={playlistType === 'music' ? 'Search the library…' : 'Search episodes…'}
            className="pl-9 bg-gray-900/60 border-gray-700 text-sm text-white placeholder:text-gray-500"
          />
        </div>

        {/* Current Track Info */}
        <div className="space-y-1.5 sm:space-y-2">
          <h4 className="text-base sm:text-xl font-semibold text-white truncate">
            {track?.title || 'No Track Selected'}
          </h4>
          <p className="text-xs sm:text-sm text-gray-400 truncate">{track?.artist}</p>
          <p className="text-xs text-gray-500 line-clamp-2 hidden sm:block">{track?.description}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-xs">
              {playlistType === 'music' ? 'Music' : 'Podcast'} • {track?.duration}
            </Badge>
            {track && !trackIsStreamable && (
              <button
                type="button"
                data-testid="inline-music-open-original"
                onClick={() => openHubLink(track.url)}
                className="inline-flex items-center gap-1 text-xs text-pink-300 hover:text-pink-200 underline underline-offset-2"
                aria-label="Open the original track in a new window"
              >
                Open original <ExternalLink className="w-3 h-3" />
              </button>
            )}
          </div>
          {track && !trackIsStreamable && (
            <p className="text-[11px] text-gray-500 italic">
              This share URL can&apos;t be streamed inline · use &ldquo;Open original&rdquo;.
            </p>
          )}
        </div>

        {/* Progress Bar */}
        <div className="space-y-1.5 sm:space-y-2">
          <div 
            className="w-full h-1.5 sm:h-2 bg-gray-700 rounded-full cursor-pointer overflow-hidden"
            onClick={handleProgressClick}
          >
            <div 
              className="h-full bg-gradient-to-r from-pink-500 to-purple-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-400">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center sm:justify-between gap-2 sm:gap-4">
          {/* Playback Controls */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              size="sm"
              variant="outline"
              onClick={skipBack}
              disabled={currentTrack === 0}
              className="w-8 h-8 sm:w-10 sm:h-10 p-0 flex-shrink-0"
            >
              <SkipBack className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>

            <Button
              onClick={togglePlay}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 p-0 flex-shrink-0"
            >
              {isPlaying ? <Pause className="w-4 h-4 sm:w-6 sm:h-6" /> : <Play className="w-4 h-4 sm:w-6 sm:h-6 ml-0.5" />}
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={skipForward}
              disabled={currentTrack === currentPlaylist.length - 1}
              className="w-8 h-8 sm:w-10 sm:h-10 p-0 flex-shrink-0"
            >
              <SkipForward className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
          </div>

          {/* Volume Control - Desktop Only */}
          <div className="hidden md:flex items-center gap-2 flex-1 max-w-xs">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsMuted(!isMuted)}
              className="w-8 h-8 p-0 flex-shrink-0"
            >
              {isMuted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </Button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={isMuted ? 0 : volume}
              onChange={(e) => {
                setVolume(parseFloat(e.target.value));
                if (isMuted) setIsMuted(false);
              }}
              className="flex-1 h-1 bg-gray-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-pink-500"
            />
          </div>
        </div>

        {/* Playlist */}
        <div className="space-y-1.5 sm:space-y-2 max-h-48 sm:max-h-64 overflow-y-auto">
          <h5 className="text-xs sm:text-sm font-semibold text-gray-400 uppercase tracking-wide sticky top-0 bg-gray-900/95 py-1">
            {playlistType === 'music' ? 'Music' : 'Podcast'} Library
            {query.trim() ? ` · ${filteredPlaylist.length} match${filteredPlaylist.length === 1 ? '' : 'es'}` : ''}
          </h5>
          {filteredPlaylist.length === 0 && (
            <div data-testid="inline-music-no-results" className="text-xs text-gray-500 italic py-2">
              No tracks match &ldquo;{query}&rdquo;.
            </div>
          )}
          {filteredPlaylist.map((item) => {
            const index = currentPlaylist.indexOf(item);
            const streamable = STREAMABLE_RE.test(item.url || '');
            return (
              <div
                key={item.id || item.url || item.title || `track-${index}`}
                data-testid={`inline-music-track-${index}`}
                onClick={() => {
                  setCurrentTrack(index);
                  setIsPlaying(streamable);
                  if (!streamable) openHubLink(item.url);
                }}
                className={`p-2 sm:p-3 rounded-lg cursor-pointer transition-all active:scale-98 ${
                  currentTrack === index
                    ? 'bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30'
                    : 'bg-gray-800/30 hover:bg-gray-800/50 active:bg-gray-800/60'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-white truncate">{item.title}</p>
                    <p className="text-xs text-gray-400 truncate">{item.artist}</p>
                  </div>
                  {!streamable && (
                    <ExternalLink className="w-3 h-3 text-pink-400 flex-shrink-0" aria-label="Opens externally" />
                  )}
                  <Badge variant="outline" className="text-xs whitespace-nowrap flex-shrink-0">
                    {item.duration}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>

        {/* Hidden Audio Element */}
        <audio
          ref={audioRef}
          src={trackIsStreamable ? track?.url : undefined}
          preload="metadata"
        />
        </>
        )}
      </div>
    </Card>
  );
};

export default InlineMusicPlayer;
